import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { Auth } from '@angular/fire/auth';
import { ArtistStatement } from './artist-statement.service';
import {
  ArtistMonthStatement,
  DeviceMonthUsage,
  STATEMENT_COMPANY_NAME,
  hasSessionsWithoutFrames,
  isBypassSession,
  statementNumber,
  statementSignature,
  toReportPeriod
} from '../utils/artist-monthly-statement.util';

const isKonoboxBrand = (id: string, data: any) =>
  [id, data?.name, data?.brandName].some(v => String(v ?? '').trim().toLowerCase() === 'konobox');

/**
 * KAN-147: reads one month of frame usage from every device (soft-deleted included) and stores issued snapshots in
 * `artist_statements` (`source: 'automated'`, one document per revision; snapshots are never rewritten).
 */
@Injectable({ providedIn: 'root' })
export class ArtistMonthlyStatementService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  /** One entry per device document (all of `devices/*` except `global`), with that month's sessions and frame counters. */
  async loadMonthUsage(period: string, onProgress?: (done: number, total: number) => void): Promise<DeviceMonthUsage[]> {
    const month = toReportPeriod(period);
    const [deviceSnap, brandSnap] = await Promise.all([
      getDocs(collection(this.firestore, 'devices')),
      getDocs(collection(this.firestore, 'brands'))
    ]);
    const konoboxBrandIds = new Set(brandSnap.docs.filter(b => isKonoboxBrand(b.id, b.data())).map(b => b.id));
    const devices = deviceSnap.docs.filter(d => d.id !== 'global');
    let done = 0;
    return Promise.all(devices.map(async d => {
      const data = d.data() || {};
      const [monthSnap, framesSnap] = await Promise.all([
        getDoc(doc(this.firestore, `devices/${d.id}/reports/${month}`)),
        getDocs(collection(this.firestore, `devices/${d.id}/reports/${month}/frames`))
      ]);
      const usage = {
        deviceId: d.id,
        deviceName: data['deviceName'] || data['name'] || d.id,
        isDeleted: data['isDeleted'] === true,
        isKonobox: konoboxBrandIds.has(String(data['brandId'] || '')),
        totalSessions: Number(monthSnap.exists() ? monthSnap.data()['TotalSessions'] : 0) || 0,
        frames: framesSnap.docs.map(f => ({
          frameId: String(f.data()['frameId'] || f.id),
          printCount: Number(f.data()['printCount']) || 0,
          themeId: f.data()['themeId'] || undefined,
          frameGroupId: f.data()['frameGroupId'] || undefined
        }))
      } as DeviceMonthUsage;
      if (hasSessionsWithoutFrames(usage)) usage.nonBypassSessions = await this.countNonBypassSessions(d.id, month);
      onProgress?.(++done, devices.length);
      return usage;
    }));
  }

  /** Non-bypassed sessions of a device in a month (`yyyy-MM`), from `devices/{id}/sessions/{yyyy-MM-dd}/sessions`. */
  private async countNonBypassSessions(deviceId: string, month: string): Promise<number> {
    const [y, m] = month.split('-').map(Number);
    const days = new Date(y, m, 0).getDate();
    const counts = await Promise.all(Array.from({ length: days }, async (_, i) => {
      const day = `${month}-${String(i + 1).padStart(2, '0')}`;
      const snap = await getDocs(collection(this.firestore, `devices/${deviceId}/sessions/${day}/sessions`));
      return snap.docs.filter(s => !isBypassSession(s.data())).length;
    }));
    return counts.reduce((a, b) => a + b, 0);
  }

  /** The latest automated revision per artist for a period. */
  async getIssued(period: string): Promise<Map<string, ArtistStatement>> {
    const snap = await getDocs(query(collection(this.firestore, 'artist_statements'), where('period', '==', period)));
    const latest = new Map<string, ArtistStatement>();
    snap.docs.forEach(d => {
      const data: any = d.data();
      for (const k of ['issuedAt', 'emailedAt', 'createdAt']) {
        if (data[k] && typeof data[k].toDate === 'function') data[k] = data[k].toDate();
      }
      const s = { id: d.id, ...data } as ArtistStatement;
      if (s.source !== 'automated' || s.isDeleted) return;
      const prev = latest.get(s.artistId);
      if (!prev || (s.revision || 0) > (prev.revision || 0)) latest.set(s.artistId, s);
    });
    return latest;
  }

  /** Saves a new revision snapshot. `imageUrls` holds the frame thumbnails shown on the statement. */
  async issue(
    period: string,
    stmt: ArtistMonthStatement,
    previous: ArtistStatement | undefined,
    missingDataDevices: string[],
    imageUrls: Map<string, string | null>
  ): Promise<ArtistStatement> {
    const revision = (previous?.revision || 0) + 1;
    const user = this.auth.currentUser;
    const snapshot: ArtistStatement = {
      period,
      companyId: '',
      companyName: STATEMENT_COMPANY_NAME,
      artistId: stmt.artistId,
      artistName: stmt.artistName,
      items: stmt.lines.map(l => ({
        frameId: l.frameId,
        frameImage: imageUrls.get(l.frameId) || '',
        quantity: l.prints,
        commission: l.rate ?? 0,
        total: l.amount ?? 0
      })),
      totalQuantity: stmt.totalPrints,
      totalAmount: stmt.totalAmount ?? 0,
      source: 'automated',
      revision,
      statementNumber: statementNumber(period, stmt.artistId, revision),
      rate: stmt.rate ?? 0,
      signature: statementSignature(stmt),
      missingDataAcknowledged: missingDataDevices.length > 0,
      missingDataDevices,
      issuedAt: new Date(),
      issuedBy: user?.email || user?.uid || 'unknown',
      emailStatus: 'not_sent',
      createdAt: new Date(),
      isDeleted: false
    };
    const ref = await addDoc(collection(this.firestore, 'artist_statements'), snapshot);
    return { ...snapshot, id: ref.id };
  }

  /** Records a queued email and follows its delivery: `Emailed` only on SUCCESS, a failure keeps `Issued`. */
  async trackEmail(statementId: string, receiptId: string, to: string, onStatus: (status: 'sent' | 'failed', error?: string) => void): Promise<() => void> {
    const statementRef = doc(this.firestore, 'artist_statements', statementId);
    await updateDoc(statementRef, { emailStatus: 'pending', emailReceiptId: receiptId, emailTo: to });
    const stop = onSnapshot(doc(this.firestore, 'email_receipts', receiptId), async snap => {
      const status = snap.data()?.['deliveryStatus'];
      if (status === 'SUCCESS') {
        stop();
        await updateDoc(statementRef, { emailStatus: 'sent', emailedAt: new Date() });
        onStatus('sent');
      } else if (status === 'ERROR') {
        stop();
        const error = String(snap.data()?.['error'] || 'Delivery failed');
        await updateDoc(statementRef, { emailStatus: 'failed', emailError: error });
        onStatus('failed', error);
      }
    });
    return stop;
  }

  /** A statement left `pending` (page closed before delivery) is settled from its email receipt on the next load. */
  async settlePendingEmail(stmt: ArtistStatement): Promise<ArtistStatement> {
    if (stmt.emailStatus !== 'pending' || !stmt.id || !stmt.emailReceiptId) return stmt;
    const receipt = await getDoc(doc(this.firestore, 'email_receipts', stmt.emailReceiptId));
    const status = receipt.data()?.['deliveryStatus'];
    if (status !== 'SUCCESS' && status !== 'ERROR') return stmt;
    const update = status === 'SUCCESS'
      ? { emailStatus: 'sent' as const, emailedAt: new Date() }
      : { emailStatus: 'failed' as const, emailError: String(receipt.data()?.['error'] || 'Delivery failed') };
    await updateDoc(doc(this.firestore, 'artist_statements', stmt.id), update);
    return { ...stmt, ...update };
  }
}
